// src/features/task/TaskActivityFeed.tsx
// Activity feed for the task detail page. Always renders (loading / empty /
// error), unlike the page-level inline block it replaces which swallowed
// ListActivity errors and hid the whole section when empty. Paginated via
// ListActivity's PaginatedRequest — supports "load more".

import { useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import { Button, Alert, Badge } from '@react/components';
import { TaskSection } from '@/features/task/detail/TaskSection';
import type { TaskActivity } from '@core/types/task';
import { formatDateTime } from '@/features/task/logic';
import { TaskActivityActor, TaskActivityChange, TaskActivityChangedFields, TaskActivityTypeLabel } from '@/features/task/TaskActivityPresentation';
import { referenceScope } from '@/features/references/cache';
import { errMsg } from '@/features/task/panelHelpers';

interface TaskActivityFeedProps {
  taskId: string;
  /** Quieter rows for the document-style detail page: smaller type, muted,
      tighter rhythm. History should read as footnotes, not as content. */
  compact?: boolean;
  refreshKey?: number;
}

const PAGE_LIMIT = 20;
export const TaskActivityFeed = observer(({ taskId, compact = false, refreshKey = 0 }: TaskActivityFeedProps) => {
  const store = useTask();
  const key = `${referenceScope()}|${taskId}`;
  const generation = useRef(0);
  const [dataKey, setDataKey] = useState('');
  const { t, locale } = useI18n();

  const [activities, setActivities] = useState<TaskActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState('');
  const [hasNext, setHasNext] = useState(false);

  const visibleActivities = dataKey === key ? activities : [];
  const load = async (append: boolean) => {
    const version = ++generation.current;
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res: any = await store.ListActivity(taskId, {
        pagination: { limit: PAGE_LIMIT, cursor: append ? cursor : '', direction: 'forward' },
        sort: [],
      } as any);
      if (version !== generation.current) return;
      setDataKey(key);
      const items: TaskActivity[] = res?.activities?.activities || [];
      setActivities((prev) => (append ? [...prev, ...items] : items));
      const pageInfo = res?.pagination?.page_info;
      setHasNext(!!pageInfo?.has_next);
      setCursor(pageInfo?.next_cursor || '');
    } catch (e) {
      if (version !== generation.current) return;
      setDataKey(key);
      setError(errMsg(e, t('task.activityFeed.loadError')));
      if (!append) setActivities([]);
    } finally {
      if (version === generation.current) {
        setLoadingMore(false);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (taskId) void load(false);
    const invalidate = () => { generation.current++; };
    return invalidate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshKey]);

  return (
    <TaskSection title={t('task.activity')}>
      <div aria-busy={loading}>
        {dataKey === key && error && <Alert variant="error">{error}</Alert>}

        {(loading || dataKey !== key) && visibleActivities.length === 0 ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : visibleActivities.length === 0 ? (
          <p className={compact ? 'text-xs text-muted-foreground' : 'text-sm text-muted-foreground'}>
            {t('task.activityFeed.empty')}
          </p>
        ) : (
          <>
            <div className="divide-y divide-border">
              {visibleActivities.map((activity: any, idx: number) => (
                <div key={activity.entity?.id || idx} className={compact ? 'py-2' : 'py-3'}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-foreground truncate`}>
                        <TaskActivityActor actor={activity.actor} />
                      </span>
                      <Badge variant="secondary" size="sm">
                        <TaskActivityTypeLabel type={activity.activity_type} field={activity.field} />
                      </Badge>
                    </div>
                    <span className={`${compact ? 'text-xs' : 'text-sm'} text-muted-foreground tabular-nums whitespace-nowrap shrink-0`}>
                      {formatDateTime(activity.created_at, locale)}
                    </span>
                  </div>
                  <TaskActivityChange activity={activity} compact={compact} />
                  {activity.metadata?.comment && (
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-muted-foreground mt-1 whitespace-pre-wrap break-words`}>
                      {activity.metadata.comment}
                    </p>
                  )}
                  {activity.metadata?.file_name && (
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-muted-foreground mt-1 break-all`}>
                      {activity.metadata.file_name}
                    </p>
                  )}
                  {activity.metadata?.fields && (
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-muted-foreground mt-1 break-words`}>
                      {t('task.activityFeed.changedFields')}: <TaskActivityChangedFields fields={activity.metadata.fields} />
                    </p>
                  )}
                </div>
              ))}
            </div>
            {hasNext && (
              <div className="pt-3 text-center">
                <Button size="sm" variant="secondary" isLoading={loadingMore} onClick={() => load(true)}>
                  {t('common.loadMore')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </TaskSection>
  );
});

export default TaskActivityFeed;
